import { registerServiceWorker } from "./serviceWorkerRegistration";
import "./Notification.css";
import "./styles.css";
import "./fonts.css";
import "./colors.css";
import "./globals.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { _queryClient } from "./lib/client";
import mixpanel from "mixpanel-browser";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import {
  Login,
  Signup,
  PrivacyPolicy,
  Profile,
  ResetPassword,
  TermsOfService,
  EditProfile,
  ChangePassword,
  OurMission,
  Widget,
} from "./pages";
import VacancyLandingPage from "./pages/public/VacancyLandingPage";
import AIVacancyLandingPage from "./pages/public/AIVacancyLandingPage";
import PublicApplyPage from "./pages/public/PublicApplyPage";
import PublicShowingSchedulePage from "./pages/public/PublicShowingSchedulePage";
import TenantIntentPage from "./pages/public/TenantIntentPage";
import VendorApplyPage from "./pages/public/VendorApplyPage";
import ApplyThankYouPage from "./pages/public/ApplyThankYouPage";
import MarketingListings from "./pages/MarketingListings";
import ProtectedRoute from "./components/ProtectedRoute";
import { ForgotPassword, SignUp } from "./components/login";
import { GOOGLE_OAUTH_CLIENT_ID } from "./helpers/authentication";
import AuthenticationRoute from "./components/AuthenticationRoute";
import { device } from "./lib/breakpoints";
import { Helmet } from "react-helmet";
import RouterErrorBoundary from "./views/RouterErrorBoundary";
import Home from "./pages/home/Home";
import Press from "./pages/Press";
import ForSelfManagingLandlords from "./pages/ForSelfManagingLandlords";
import { APP_NAME } from "./helpers/constants";
import Contact from "./views/Contact";
import Dashboard from "./pages/dashboard/Dashboard";
import OnboardingSurveyPage from "./pages/OnboardingSurveyPage";
import LeaseUpsPage from "./pages/LeaseUpsPage";
import CRMDashboard from "./pages/crm/CRMDashboard";
import Units from "./pages/Units";
import Tenants from "./pages/Tenants";
import Settings from "./pages/Settings";
import RentDashboard from "./pages/RentDashboard";
import TenantPaymentPortal from "./pages/TenantPaymentPortal";
import TenantRentPayment from "./pages/TenantRentPayment";
import TenantView from "./pages/TenantView";
import TenantLedgerView from "./pages/TenantLedgerView";
import PaymentSuccess from "./pages/PaymentSuccess";
import RentPaymentStatus from "./pages/RentPaymentStatus";
import PaymentReturn from "./pages/PaymentReturn";
import ReminderSettings from "./pages/ReminderSettings";
import AIAutomationDashboard from "./pages/AIAutomationDashboard";
import Offers from "./pages/Offers";
import Agents from "./pages/Agents";
import Referrals from "./pages/settings/Referrals";
import PaymentSetup from "./pages/PaymentSetup";
import { APP_TERMS_ROUTE } from "./helpers/routeNames";
import MainLayout from "./views/MainLayout";
import NotFound from "./views/NotFound";
import Unsubscribe from "./views/Unsubscribe";
import Clients from "./pages/Clients";
import BookingSettings from "./pages/BookingSettings";
import EmbedCode from "./pages/EmbedCode";
import PurchaseSuccess from "./views/PurchaseSuccess";
import PurchaseCancelled from "./views/PurchaseCancelled";
import Leases from "./pages/Leases";
import Repairs from "./pages/Repairs";
import BiddingDashboard from "./pages/BiddingDashboard";
import Communication from "./pages/Communication";
import TenantChat from "./pages/TenantChat";
import VendorChatDashboard from "./pages/VendorChatDashboard";
import NotificationSettings from "./pages/NotificationSettings";
import AccessDenied from "./pages/AccessDenied";
import TokenExpired from "./pages/TokenExpired";
import VendorBidPage from "./pages/VendorBidPage";
import VendorNegotiationPage from "./pages/public/VendorNegotiationPage";
import LegacyPricingPage from "./pages/exclusive/LegacyPricingPage";
import VendorBidSuccess from "./pages/VendorBidSuccess";
import VendorConversationPage from "./pages/VendorConversationPage";
import RepairConversationRedirect from "./pages/RepairConversationRedirect";
import BidComparisonScreenshotPage from "./pages/BidComparisonScreenshotPage";
import BackgroundCheckSuccess from "./pages/BackgroundCheckSuccess";
import BackgroundCheckError from "./pages/BackgroundCheckError";
import TaxPrepPage from "./pages/TaxPrepPage";
import ClosingVaultPage from "./pages/ClosingVaultPage";
import Expenses from "./pages/Expenses";
import DealAnalyzer from "./pages/DealAnalyzer";
import QuickActionPage from "./pages/QuickActionPage";
import SetupVendorPage from "./pages/SetupVendorPage";
import DemoPage from "./app/demo/page";
import OpsChatPage from "./pages/OpsChatPage";
import V2SimulationScreen from "./pages/v2/V2SimulationScreen";
import V2Dashboard from "./pages/v2/V2Dashboard";

mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
  debug: !process.env.NODE_ENV || process.env.NODE_ENV === "development",
  track_pageview: true,
  persistence: "localStorage",
  ignore_dnt: true,
});

const router = createBrowserRouter([
  {
    element: <MainLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/platform",
        element: <Navigate to="/#product-features" replace />,
      },
      {
        path: "/for-self-managing-landlords",
        element: <ForSelfManagingLandlords />,
      },
      {
        path: "/opschat",
        element: <OpsChatPage />,
      },
      {
        path: "/press",
        element: <Press />,
      },
      {
        path: "/privacy",
        element: <PrivacyPolicy />,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        path: APP_TERMS_ROUTE,
        element: <TermsOfService />,
      },
      {
        path: "/contact",
        element: <Contact />,
      },
      {
        path: "/unsubscribe",
        element: <Unsubscribe />,
      },
      {
        path: "/agents",
        element: <Agents />,
      },
      {
        path: "/purchase-success",
        element: <PurchaseSuccess />,
      },
      {
        path: "/purchase-cancelled",
        element: <PurchaseCancelled />,
      },
      {
        path: "/pay-rent/:id",
        element: <TenantRentPayment />,
      },
      {
        path: "/rent-payment/:id",
        element: <TenantRentPayment />,
      },
      {
        path: "/rent-payment/:id/status",
        element: <RentPaymentStatus />,
      },
      {
        path: "/tenant-view/:id",
        element: <TenantView />,
      },
      {
        path: "/tenant-ledger/:tenantId",
        element: <TenantLedgerView />,
      },
      {
        path: "/tenant-chat/:tenantId",
        element: <TenantChat />,
      },
      {
        path: "/bid/:token",
        element: <VendorBidPage />,
      },
      {
        path: "/negotiate/:bidToken",
        element: <VendorNegotiationPage />,
      },
      {
        path: "/buyersclub",
        element: <Navigate to="/legacy" replace />,
      },
      {
        path: "/legacy",
        element: <LegacyPricingPage />,
      },
      {
        path: "/chat/:propertySlug",
        element: <TenantChat />,
      },
      {
        path: "/quick-action/:token",
        element: <QuickActionPage />,
      },
      {
        path: "/bid/success",
        element: <VendorBidSuccess />,
      },
      {
        path: "/conversations/:conversationId",
        element: <VendorConversationPage />,
      },
      {
        path: "/repairs/:ticketId",
        element: <RepairConversationRedirect />,
      },
      {
        path: "/access-denied",
        element: <AccessDenied />,
      },
      {
        path: "/token-expired",
        element: <TokenExpired />,
      },
      {
        path: "/payment-success",
        element: <PaymentSuccess />,
      },
      {
        path: "/lease/:listingId", // listingId refers to listing ID in listings collection
        element: <AIVacancyLandingPage />,
      },
      {
        path: "/apply/:listingId",
        element: <PublicApplyPage />,
      },
      {
        path: "/apply/thank-you",
        element: <ApplyThankYouPage />,
      },
      {
        path: "/p/:slug/schedule",
        element: <PublicShowingSchedulePage />,
      },
      {
        path: "/tenant-intent/:token",
        element: <TenantIntentPage />,
      },
      {
        path: "/vendors/apply",
        element: <VendorApplyPage />,
      },
      {
        path: "/payments/return",
        element: <PaymentReturn />,
      },
      {
        path: "/payment-setup",
        element: <PaymentSetup />,
      },
      {
        path: "/background-check/success",
        element: <BackgroundCheckSuccess />,
      },
      {
        path: "/background-check/error",
        element: <BackgroundCheckError />,
      },
      {
        // Public outbound-lead personalised demo; no auth required
        path: "/demo",
        element: <DemoPage />,
      },
      {
        // requireAccount ensures isLimitedToOpsChatExperience check fires,
        // blocking Free Gateway + OpsChat-only users from Full OS pages.
        element: <ProtectedRoute requireAccount />,
        children: [
          { path: "/widget", element: <Widget /> },
          { path: "/crm", element: <CRMDashboard /> },
          { path: "/clients", element: <Clients /> },
          { path: "/booking-settings", element: <BookingSettings /> },
          { path: "/embed-code", element: <EmbedCode /> },
          { path: "/property-dashboard", element: <Dashboard /> },
          { path: "/vendor-chat", element: <VendorChatDashboard /> },
        ],
      },
      {
        path: "/login",
        element: (
          <AuthenticationRoute>
            <Login />
          </AuthenticationRoute>
        ),
      },
      {
        path: "/signup",
        element: (
          <AuthenticationRoute>
            <Signup />
          </AuthenticationRoute>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <AuthenticationRoute>
            <ForgotPassword />
          </AuthenticationRoute>
        ),
      },
      // V2 bolt-on "AI Front Desk" routes (gated: must be logged in)
      {
        element: <ProtectedRoute requireAccount />,
        children: [
          { path: "/v2/simulation", element: <V2SimulationScreen /> },
          { path: "/v2/dashboard", element: <V2Dashboard /> },
        ],
      },
      // Paths that require a user to be logged in
      {
        element: <ProtectedRoute requireAccount />,
        children: [
          { path: "/dashboard", element: <Dashboard /> },
          { path: "/units", element: <Units /> },
          { path: "/tenants", element: <Tenants /> },
          { path: "/rent", element: <RentDashboard /> },
          { path: "/communication", element: <Communication /> },
          { path: "/communication/:tenantId", element: <Communication /> },
          { path: "/leases", element: <Leases /> },
          { path: "/repairs", element: <Repairs /> },
          {
            path: "/demo/bid-comparison",
            element: <BidComparisonScreenshotPage />,
          },
          {
            path: "/properties/:propertyId/setup-vendor",
            element: <SetupVendorPage />,
          },
          { path: "/expenses", element: <Expenses /> },
          { path: "/bids", element: <BiddingDashboard /> },
          { path: "/rent/tenant", element: <TenantPaymentPortal /> },
          { path: "/reminders", element: <ReminderSettings /> },
          { path: "/leo", element: <AIAutomationDashboard /> },
          { path: "/offers", element: <Offers /> },
          { path: "/listings", element: <MarketingListings /> },
          { path: "/lease-ups", element: <LeaseUpsPage /> },
          { path: "/analyzer", element: <DealAnalyzer /> },
          { path: "/tax-prep", element: <TaxPrepPage /> },
          { path: "/closing-vault", element: <ClosingVaultPage /> },
          { path: "/settings", element: <Settings /> },
          { path: "/settings/referrals", element: <Referrals /> },
          {
            path: "/profile",
            element: <Profile />,
          },
          { path: "/profile/edit", element: <EditProfile /> },
          { path: "/profile/change-password", element: <ChangePassword /> },
          {
            path: "/survey/first-impressions",
            element: <OnboardingSurveyPage />,
          },
          // { path: "/visualizer", element: <Visualizer /> },
        ],
      },
      {
        path: "/notification-settings",
        element: (
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

registerServiceWorker();

ReactDOM.createRoot(document.getElementById("root")).render(
  <>
    <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
      <QueryClientProvider client={_queryClient}>
        <RouterProvider router={router} />
        {/* <ReactQueryDevtools buttonPosition="bottom-left" /> */}
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </>
);
